import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './AboutUsPageSettings.css';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const iconOptions = [
  { value: 'shieldCheck', label: 'Shield Check (Mission)' },
  { value: 'compass', label: 'Compass (Vision)' },
  { value: 'target', label: 'Target' },
  { value: 'award', label: 'Award' },
];

function AboutUsPageSettings() {
  const [heroTitle, setHeroTitle] = useState('');
  const [heroImage, setHeroImage] = useState('');
  const [sectionTag, setSectionTag] = useState('');
  const [sectionTitle, setSectionTitle] = useState('');
  const [sectionDescription, setSectionDescription] = useState('');
  const [sectionHighlight, setSectionHighlight] = useState('');
  const [cards, setCards] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/about-us-page`);
        const d = res.data || {};
        setHeroTitle(d.heroTitle || '');
        setHeroImage(d.heroImage || '');
        setSectionTag(d.sectionTag || '');
        setSectionTitle(d.sectionTitle || '');
        setSectionDescription(d.sectionDescription || '');
        setSectionHighlight(d.sectionHighlight || '');
        setCards(Array.isArray(d.cards) ? d.cards : []);
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPage();
  }, []);

  const setCard = (index, field, value) => {
    setCards((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleAddCard = () => {
    setCards((prev) => [
      ...prev,
      { id: 'card-' + Date.now(), title: '', text: '', iconKey: 'shieldCheck' },
    ]);
  };

  const handleDeleteCard = (index) => {
    setCards((prev) => prev.filter((_, i) => i !== index));
  };

  const handleHeroImageUpload = async (e) => {
    const file = e && e.target && e.target.files && e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('image', file);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/about-us-page/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (res.data && res.data.url) setHeroImage(res.data.url);
    } catch (err) {
      console.error(err);
      setMessage('Upload failed.');
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMessage('');
    try {
      await axios.put(`${API_BASE_URL}/api/about-us-page`, {
        heroTitle,
        heroImage,
        sectionTag,
        sectionTitle,
        sectionDescription,
        sectionHighlight,
        cards,
      });
      setMessage('About Us page updated successfully.');
    } catch (err) {
      console.error(err);
      setMessage('Unable to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <p className="about-us-page-settings-loading">Loading About Us page…</p>
    );
  }

  return (
    <div className="about-us-page-settings">
      <div className="about-us-page-settings-header">
        <div>
          <h2 className="about-us-page-settings-title">About Us page</h2>
          <p className="about-us-page-settings-subtitle">
            Edit the /about page: hero, main text, and mission/vision cards.
            Add or remove cards as needed.
          </p>
        </div>
        <button
          type="button"
          className="about-us-page-settings-save-btn"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? 'Saving…' : 'Save changes'}
        </button>
      </div>

      <div className="about-us-page-settings-hero">
        <h3 className="about-us-page-settings-section-title">Hero</h3>
        <label className="about-us-page-settings-label">
          Hero title
          <input
            type="text"
            className="about-us-page-settings-input"
            value={heroTitle}
            onChange={(e) => setHeroTitle(e.target.value)}
            placeholder="About Us"
          />
        </label>
        <label className="about-us-page-settings-label">
          Hero image URL
          <input
            type="text"
            className="about-us-page-settings-input"
            value={heroImage}
            onChange={(e) => setHeroImage(e.target.value)}
            placeholder="https://... or leave empty for default"
          />
        </label>
        <label className="about-us-page-settings-label">
          Or upload hero image
          <input type="file" accept="image/*" onChange={handleHeroImageUpload} />
        </label>
      </div>

      <div className="about-us-page-settings-main">
        <h3 className="about-us-page-settings-section-title">Main content</h3>
        <label className="about-us-page-settings-label">
          Section tag (eyebrow)
          <input
            type="text"
            className="about-us-page-settings-input"
            value={sectionTag}
            onChange={(e) => setSectionTag(e.target.value)}
            placeholder="ABOUT WHALES ADVISORY"
          />
        </label>
        <label className="about-us-page-settings-label">
          Section title (new line = line break)
          <textarea
            className="about-us-page-settings-textarea"
            rows={2}
            value={sectionTitle}
            onChange={(e) => setSectionTitle(e.target.value)}
            placeholder="Dedicated Legal Support"
          />
        </label>
        <label className="about-us-page-settings-label">
          Section description
          <textarea
            className="about-us-page-settings-textarea"
            rows={4}
            value={sectionDescription}
            onChange={(e) => setSectionDescription(e.target.value)}
          />
        </label>
        <label className="about-us-page-settings-label">
          Section highlight paragraph
          <textarea
            className="about-us-page-settings-textarea"
            rows={4}
            value={sectionHighlight}
            onChange={(e) => setSectionHighlight(e.target.value)}
          />
        </label>
      </div>

      <div className="about-us-page-settings-cards">
        <div className="about-us-page-settings-title-row">
          <h3 className="about-us-page-settings-section-title">
            Cards (Mission / Vision)
          </h3>
          <button
            type="button"
            className="about-us-page-settings-secondary-btn"
            onClick={handleAddCard}
          >
            + Add card
          </button>
        </div>
        {cards.map((card, index) => (
          <div key={card.id || index} className="about-us-page-settings-card">
            <div className="about-us-page-settings-card-actions">
              <button
                type="button"
                className="about-us-page-settings-danger-btn"
                onClick={() => handleDeleteCard(index)}
              >
                Remove card
              </button>
            </div>
            <label className="about-us-page-settings-label">
              Card title
              <input
                type="text"
                className="about-us-page-settings-input"
                value={card.title || ''}
                onChange={(e) => setCard(index, 'title', e.target.value)}
                placeholder="Our Mission"
              />
            </label>
            <label className="about-us-page-settings-label">
              Card text
              <textarea
                className="about-us-page-settings-textarea"
                rows={3}
                value={card.text || ''}
                onChange={(e) => setCard(index, 'text', e.target.value)}
              />
            </label>
            <label className="about-us-page-settings-label">
              Icon
              <select
                className="about-us-page-settings-input"
                value={card.iconKey || 'shieldCheck'}
                onChange={(e) => setCard(index, 'iconKey', e.target.value)}
              >
                {iconOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        ))}
      </div>

      {message && (
        <p className="about-us-page-settings-message">{message}</p>
      )}
    </div>
  );
}

export default AboutUsPageSettings;
